import {
  Activity,
  Bell,
  Bot,
  ChevronDown,
  CircleHelp,
  ClipboardList,
  Command,
  Crown,
  Gamepad2,
  LayoutDashboard,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  SquarePlus,
  Trophy,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import type { AuthUser } from "../lib/api";
import { NavLink, useLocation } from "../lib/router";

const primaryNav = [
  { to: "/", icon: LayoutDashboard, label: "Ringkasan", end: true, permission: "dashboard.view" },
  { to: "/questions/new", icon: SquarePlus, label: "Tambah soal", permission: "question.create" },
  { to: "/questions", icon: ClipboardList, label: "Bank soal", permission: "question.view", end: true },
  { to: "/live", icon: Gamepad2, label: "Live game", permission: "game.start" },
  { to: "/groups", icon: Users, label: "Grup Telegram", permission: "group.manage" },
  { to: "/leaderboard", icon: Trophy, label: "Leaderboard", permission: "leaderboard.view" },
];

const systemNav = [
  { to: "/admins", icon: UserCog, label: "Administrator", permission: "admin.manage" },
  { to: "/audit", icon: ShieldCheck, label: "Audit log", permission: "audit.view" },
  { to: "/settings", icon: Settings, label: "Pengaturan", permission: "settings.manage" },
];

const pageMeta: Record<string, { eyebrow: string; title: string }> = {
  "/": { eyebrow: "Command center", title: "Selamat datang, Rendi" },
  "/questions": { eyebrow: "Konten permainan", title: "Bank soal" },
  "/questions/new": { eyebrow: "Question studio", title: "Buat soal baru" },
  "/live": { eyebrow: "Real-time operations", title: "Live game" },
  "/groups": { eyebrow: "Telegram network", title: "Grup terhubung" },
  "/leaderboard": { eyebrow: "Player intelligence", title: "Leaderboard" },
  "/audit": { eyebrow: "Security & compliance", title: "Audit log" },
  "/admins": { eyebrow: "Access control", title: "Administrator" },
  "/settings": { eyebrow: "Workspace", title: "Pengaturan" },
};

function Navigation({ user, mobile = false, onNavigate }: { user: AuthUser; mobile?: boolean; onNavigate?: () => void }) {
  const can = (permission: string) => user.permissions.includes(permission);
  const home = can("dashboard.view") ? "/" : "/questions/new";
  const initials = user.displayName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  return (
    <>
      <div className="sidebar-brand">
        <div className="brand-mark"><Crown size={19} strokeWidth={2.3} /></div>
        <div className="brand-copy">
          <strong>Siapa Dia?</strong>
          <span>Master control</span>
        </div>
        {mobile && <button className="icon-button ghost mobile-close" onClick={onNavigate}><X size={18} /></button>}
      </div>

      <NavLink to={can("settings.manage") ? "/settings" : home} onClick={onNavigate} className="workspace-switcher">
        <span className="workspace-avatar">SD</span>
        <span><strong>Production</strong><small>Telegram Game</small></span>
        <ChevronDown size={15} />
      </NavLink>

      <nav className="sidebar-nav" aria-label="Navigasi utama">
        <span className="nav-section-label">Workspace</span>
        {primaryNav.filter((item) => can(item.permission)).map(({ icon: Icon, permission: _, ...item }) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <Icon size={18} strokeWidth={1.9} /><span>{item.label}</span>
            {item.to === "/live" && <i className="live-dot" />}
          </NavLink>
        ))}
        <span className="nav-section-label nav-section-gap">Sistem</span>
        {systemNav.filter((item) => can(item.permission)).map(({ icon: Icon, permission: _, ...item }) => (
          <NavLink key={item.to} to={item.to} onClick={onNavigate} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <Icon size={18} strokeWidth={1.9} /><span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-spacer" />
      <div className="bot-health-card">
        <div className="bot-health-top"><span className="status-pulse" /><span>Bot online</span><strong>99.99%</strong></div>
        <p>Webhook aman dan merespons normal.</p>
        <div className="health-bar"><span /></div>
      </div>
      <NavLink to={can("settings.manage") ? "/settings" : home} onClick={onNavigate} className="sidebar-profile">
        <span className="profile-avatar">{initials || "AD"}</span>
        <span><strong>{user.displayName}</strong><small>{user.role === "QUESTION_CREATOR" ? "Penambah soal" : user.role === "CONTENT_PUBLISHER" ? "Publisher soal" : user.role}</small></span>
        <ChevronDown size={15} />
      </NavLink>
    </>
  );
}

export function AppShell({ user, children }: { user: AuthUser; children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [panel, setPanel] = useState<"help" | "notifications" | null>(null);
  const location = useLocation();
  const meta = pageMeta[location.pathname] ?? pageMeta["/"];

  useEffect(() => setDrawerOpen(false), [location.pathname]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((value) => !value);
      }
      if (event.key === "Escape") setCommandOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className="sidebar"><Navigation user={user} /></aside>
      {drawerOpen && <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />}
      <aside className={`mobile-drawer ${drawerOpen ? "open" : ""}`}><Navigation user={user} mobile onNavigate={() => setDrawerOpen(false)} /></aside>

      <div className="app-main">
        <header className="topbar">
          <button className="icon-button ghost mobile-menu" onClick={() => setDrawerOpen(true)} aria-label="Buka menu"><Menu size={20} /></button>
          <button className="icon-button ghost collapse-control" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? "Buka sidebar" : "Ciutkan sidebar"}>{collapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}</button>
          <button className="command-trigger" onClick={() => setCommandOpen(true)}>
            <Search size={17} /><span>Cari apa saja...</span><kbd><Command size={11} /> K</kbd>
          </button>
          <div className="topbar-actions">
            <div className="environment-pill"><span /> Sistem optimal</div>
            <button className="icon-button ghost" title="Bantuan" onClick={() => setPanel("help")}><CircleHelp size={19} /></button>
            <button className="icon-button ghost notification-button" title="Notifikasi" onClick={() => setPanel("notifications")}><Bell size={19} /><i /></button>
          </div>
        </header>

        <main className="content-wrap">
          <div className="page-heading">
            <div><p className="eyebrow">{meta.eyebrow}</p><h1>{meta.title}</h1></div>
            <div className="page-date"><span>Waktu lokal</span><strong>{new Intl.DateTimeFormat("id-ID", { weekday: "long", day: "2-digit", month: "long" }).format(new Date())}</strong></div>
          </div>
          {children}
        </main>
      </div>

      {commandOpen && (
        <div className="modal-layer" onMouseDown={() => setCommandOpen(false)}>
          <div className="command-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="command-input"><Search size={19} /><input autoFocus placeholder="Cari halaman, soal, grup, atau aksi..." /></div>
            <div className="command-results">
              <span className="nav-section-label">Aksi cepat</span>
              {user.permissions.includes("question.create") && <NavLink to="/questions/new" onClick={() => setCommandOpen(false)}><Sparkles size={18} /><span><strong>Buat soal baru</strong><small>Buka question studio</small></span><kbd>N</kbd></NavLink>}
              {user.permissions.includes("game.start") && <NavLink to="/live" onClick={() => setCommandOpen(false)}><Activity size={18} /><span><strong>Pantau live game</strong><small>Lihat ronde aktif</small></span></NavLink>}
              {user.permissions.includes("group.manage") && <NavLink to="/groups" onClick={() => setCommandOpen(false)}><Bot size={18} /><span><strong>Kelola grup</strong><small>Pengaturan bot Telegram</small></span></NavLink>}
            </div>
          </div>
        </div>
      )}
      {panel && <div className="modal-layer" onMouseDown={() => setPanel(null)}><div className="info-modal" onMouseDown={(event) => event.stopPropagation()}><header><div><p className="eyebrow">{panel === "help" ? "Help center" : "Activity center"}</p><h3>{panel === "help" ? "Panduan cepat" : "Notifikasi"}</h3></div><button className="icon-button ghost" onClick={() => setPanel(null)}><X size={17} /></button></header>{panel === "help" ? <div className="info-modal-body"><p>{user.role === "QUESTION_CREATOR" ? "Unggah foto dan lengkapi jawaban. Soal akan masuk sebagai draft untuk ditinjau Master." : "Gunakan control center untuk mengelola bot dan permainan Telegram."}</p>{user.permissions.includes("settings.manage") && <NavLink to="/settings" onClick={() => setPanel(null)} className="button secondary">Buka konfigurasi lengkap</NavLink>}</div> : <div className="info-modal-body"><div className="notification-item"><span className="status-pulse" /><div><strong>Sistem siap digunakan</strong><p>Belum ada peringatan operasional baru.</p></div></div>{user.permissions.includes("audit.view") && <NavLink to="/audit" onClick={() => setPanel(null)} className="button secondary">Lihat audit log</NavLink>}</div>}</div></div>}
    </div>
  );
}
